import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, LinearProgress, Stack, TextField, Typography } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { apiErrorMessage } from '../api';
import { help } from '../helpTexts';
import { launchColors } from '../launchTheme';
import { dateTimeText, personName } from '../productInfo/productLabels';
import InfoTip from '../ui/InfoTip';
import { alcoholApi } from './alcoholApi';
import { alcoholCommandId, counterColor } from './alcoholLabels';
import { AlcoholPrompts, AlcoholSettings, PROMPT_KEYS, PROMPT_LABELS, PROMPT_LIMIT, PromptKey } from './alcoholTypes';
import TextHistoryDialog from './TextHistoryDialog';

interface Props {
  open: boolean;
  canSave: boolean;
  names: Record<string, string>;
  onClose: () => void;
  onSaved: (settings: AlcoholSettings) => void;
}

const HINTS: Record<PromptKey, string> = {
  description: 'Skriver om texten i rutan Beskrivning.',
  shortDescription: 'Kortar den nya långa texten till högst 300 tecken.',
  winery: 'Skriver om texten i rutan Beskrivning vingård.',
};

const loadHistory = () => alcoholApi.settingsHistory();

const PromptSettingsDialog: React.FC<Props> = ({ open, canSave, names, onClose, onSaved }) => {
  const [settings, setSettings] = useState<AlcoholSettings | null>(null);
  const [drafts, setDrafts] = useState<AlcoholPrompts | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [historyKey, setHistoryKey] = useState<PromptKey | null>(null);

  const load = useCallback(() => {
    setSettings(null);
    setDrafts(null);
    alcoholApi.settings()
      .then((next) => { setSettings(next); setDrafts(next.prompts); })
      .catch((requestError) => setError(apiErrorMessage(requestError, 'Promptinställningarna kunde inte hämtas.')));
  }, []);

  useEffect(() => {
    if (!open) return;
    setError(null);
    load();
  }, [load, open]);

  const changed = Boolean(settings && drafts && PROMPT_KEYS.some((key) => drafts[key] !== settings.prompts[key]));
  const tooLong = Boolean(drafts && PROMPT_KEYS.some((key) => drafts[key].length > PROMPT_LIMIT));

  const save = async () => {
    if (!settings || !drafts) return;
    setBusy(true);
    setError(null);
    try {
      const next = await alcoholApi.saveSettings({ prompts: drafts, expectedVersion: settings.version, commandId: alcoholCommandId() });
      setSettings(next);
      setDrafts(next.prompts);
      onSaved(next);
    } catch (requestError: any) {
      if (requestError?.response?.status === 409) {
        setError('Någon annan hann ändra prompterna. De har laddats om, så gör din ändring igen.');
        load();
      } else {
        setError(apiErrorMessage(requestError, 'Prompterna kunde inte sparas.'));
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <span>Promptinställningar</span>
          <InfoTip text={help.alcoholPrompts} />
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {!canSave && (
            <Alert severity="info" icon={<LockOutlinedIcon fontSize="inherit" />}>Bara administratörer kan ändra prompterna.</Alert>
          )}
          {error && <Alert severity="error">{error}</Alert>}
          {(!drafts || busy) && !error && <LinearProgress />}
          {drafts && PROMPT_KEYS.map((key) => (
            <Box key={key}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                maxRows={14}
                label={PROMPT_LABELS[key]}
                value={drafts[key]}
                placeholder="Klistra in prompten här."
                onChange={(event) => setDrafts({ ...drafts, [key]: event.target.value })}
                InputProps={{ readOnly: !canSave }}
                error={drafts[key].length > PROMPT_LIMIT}
                sx={{ bgcolor: canSave ? launchColors.card : launchColors.section }}
              />
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <Typography sx={{ fontSize: 12, color: launchColors.textSecondary, flex: 1 }}>
                  {drafts[key].trim() ? HINTS[key] : 'Tom. Gör ny text är avstängd för det här fältet.'}
                </Typography>
                <Typography sx={{ fontSize: 12, color: counterColor(drafts[key].length, PROMPT_LIMIT), fontVariantNumeric: 'tabular-nums' }}>
                  {drafts[key].length}/{PROMPT_LIMIT}
                </Typography>
                <Button size="small" startIcon={<HistoryIcon />} onClick={() => setHistoryKey(key)}>Historik</Button>
              </Stack>
            </Box>
          ))}
          {settings && settings.version > 0 && (
            <Typography sx={{ fontSize: 12, color: launchColors.textSecondary }}>
              Senast sparad {[personName(settings.updatedBy, names), dateTimeText(settings.updatedAt)].filter(Boolean).join(' · ')}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>{canSave ? 'Avbryt' : 'Stäng'}</Button>
        {canSave && <Button variant="contained" disabled={busy || !changed || tooLong} onClick={save}>Spara</Button>}
      </DialogActions>
      <TextHistoryDialog
        open={Boolean(historyKey)}
        title={historyKey ? `Historik: ${PROMPT_LABELS[historyKey]}` : ''}
        label={historyKey ? PROMPT_LABELS[historyKey] : ''}
        current={historyKey && drafts ? drafts[historyKey] : ''}
        names={names}
        canRestore={canSave}
        load={loadHistory}
        onRestore={(text) => {
          if (historyKey && drafts) setDrafts({ ...drafts, [historyKey]: text });
          setHistoryKey(null);
        }}
        onClose={() => setHistoryKey(null)}
      />
    </Dialog>
  );
};

export default PromptSettingsDialog;
